import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const forced = Boolean(user?.must_change_password);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("New password and confirmation do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated successfully");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-10 w-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white">
          <Building2 className="h-5 w-5" />
        </div>
        <p className="font-heading font-bold text-lg text-slate-900">Tamanna Sales Hub</p>
      </div>

      <Card className="w-full max-w-md p-6 sm:p-8 border-slate-200 shadow-sm" data-testid="change-password-card">
        <div className="flex items-center gap-2 text-[#1E3A8A] mb-1">
          <ShieldCheck className="h-5 w-5" />
          <h1 className="font-heading text-xl font-bold text-slate-900">
            {forced ? "Set a new password" : "Change password"}
          </h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          {forced
            ? "For security, you must replace the default password before accessing your dashboard."
            : "Update your account password."}
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-2">
            <Label>Current password</Label>
            <Input
              type="password"
              required
              data-testid="current-password-input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="bg-white"
            />
          </div>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input
              type="password"
              required
              data-testid="new-password-input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="bg-white"
              placeholder="Min 8 chars, 1 letter & 1 number"
            />
          </div>
          <div className="space-y-2">
            <Label>Confirm new password</Label>
            <Input
              type="password"
              required
              data-testid="confirm-password-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-white"
            />
          </div>

          {error && (
            <p data-testid="change-password-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit"
            className="w-full h-11 bg-[#1E3A8A] hover:bg-[#172554]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </form>

        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={forced ? logout : () => navigate("/")}
            data-testid="change-password-back"
            className="text-xs text-slate-400 hover:text-slate-600"
          >
            {forced ? "Log out" : "Back to dashboard"}
          </button>
        </div>
      </Card>
    </div>
  );
}
